import { createRoot } from "react-dom/client"

/**
 * Confirmação modal que FUNCIONA dentro do iframe sandbox da app.
 * `window.confirm/alert/prompt` são ignorados em iframes sandbox (sem
 * `allow-modals`) — devolvem false silenciosamente, o que fazia os botões
 * "Eliminar" não fazerem nada. Este substitui-os com um diálogo próprio.
 *
 *   if (!(await confirmDialog("Eliminar?"))) return
 */
export function confirmDialog(
  message: string,
  opts: { title?: string; confirmLabel?: string; cancelLabel?: string; destructive?: boolean } = {},
): Promise<boolean> {
  return new Promise((resolve) => {
    const host = document.createElement("div")
    document.body.appendChild(host)
    const root = createRoot(host)
    const done = (v: boolean) => {
      resolve(v)
      // desmonta no próximo tick para não cortar o handler de clique
      setTimeout(() => {
        root.unmount()
        host.remove()
      }, 0)
    }

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") done(false)
    }
    document.addEventListener("keydown", onKey, { once: true })

    root.render(
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) done(false)
        }}
      >
        <div className="w-full max-w-sm rounded-lg border border-border bg-background p-5 shadow-xl">
          {opts.title && <h2 className="mb-1 text-base font-semibold">{opts.title}</h2>}
          <p className="text-sm text-muted-foreground">{message}</p>
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => done(false)}
              className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
            >
              {opts.cancelLabel ?? "Cancelar"}
            </button>
            <button
              onClick={() => done(true)}
              className={
                "rounded-md px-3 py-1.5 text-sm font-medium text-white " +
                (opts.destructive ? "bg-destructive hover:opacity-90" : "bg-primary hover:opacity-90")
              }
            >
              {opts.confirmLabel ?? "Confirmar"}
            </button>
          </div>
        </div>
      </div>,
    )
  })
}

/**
 * Pergunta com texto, no lugar do `window.prompt` (que o iframe sandbox também
 * ignora — devolve null sem mostrar nada). Devolve o texto, ou null se cancelar.
 *
 *   const motivo = await promptDialog("Motivo da paragem")
 */
export function promptDialog(
  message: string,
  opts: { title?: string; initial?: string; confirmLabel?: string; cancelLabel?: string } = {},
): Promise<string | null> {
  return new Promise((resolve) => {
    const host = document.createElement("div")
    document.body.appendChild(host)
    const root = createRoot(host)
    let value = opts.initial ?? ""
    const done = (v: string | null) => {
      document.removeEventListener("keydown", onKey)
      resolve(v)
      setTimeout(() => {
        root.unmount()
        host.remove()
      }, 0)
    }

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") done(null)
    }
    document.addEventListener("keydown", onKey)

    root.render(
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) done(null)
        }}
      >
        <form
          className="w-full max-w-sm rounded-lg border border-border bg-background p-5 shadow-xl"
          onSubmit={(e) => {
            e.preventDefault()
            done(value)
          }}
        >
          {opts.title && <h2 className="mb-1 text-base font-semibold">{opts.title}</h2>}
          <label className="block text-sm text-muted-foreground">
            {message}
            <textarea
              autoFocus
              defaultValue={value}
              onChange={(e) => {
                value = e.target.value
              }}
              rows={3}
              className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-base text-foreground"
            />
          </label>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => done(null)} className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted">
              {opts.cancelLabel ?? "Cancelar"}
            </button>
            <button type="submit" className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90">
              {opts.confirmLabel ?? "OK"}
            </button>
          </div>
        </form>
      </div>,
    )
  })
}
